import { apiClient } from '../client'
import type {
  ChallengeCodePayload,
  DisablePayload,
  EnrollPayload,
  RecoveryCodesResponse,
  TwoFactorEnrollment,
  TwoFactorStatus,
} from '../../app/modules/types/twoFactor'

/**
 * Two-factor endpoints. Every `/auth/2fa/` failure is rendered in place by its
 * screen (see the exemption in `SetupAxios`), so none of these toast.
 *
 * `challenge/*` are unauthenticated and need no CSRF; the rest are normal
 * signed-in calls.
 */
export const twoFactorApi = {
  /** Enrolled user: the code (or a recovery code) completes sign-in; cookies arrive on the 204. */
  verifyChallenge: async (payload: ChallengeCodePayload): Promise<void> => {
    await apiClient.post('/auth/2fa/challenge/verify/', payload)
  },

  /** User who must enrol: mints a NEW secret on every call. */
  enrollChallenge: async (challengeToken: string): Promise<TwoFactorEnrollment> => {
    const { data } = await apiClient.post<TwoFactorEnrollment>('/auth/2fa/challenge/enroll/', {
      challenge_token: challengeToken,
    })
    return data
  },

  /** Confirms the first code: enrolled AND signed in (cookies set); returns the recovery codes once. */
  confirmChallengeEnrollment: async (payload: ChallengeCodePayload): Promise<RecoveryCodesResponse> => {
    const { data } = await apiClient.post<RecoveryCodesResponse>('/auth/2fa/challenge/enroll/confirm/', payload)
    return data
  },

  status: async (): Promise<TwoFactorStatus> => {
    const { data } = await apiClient.get<TwoFactorStatus>('/auth/2fa/')
    return data
  },

  enroll: async (payload: EnrollPayload): Promise<TwoFactorEnrollment> => {
    const { data } = await apiClient.post<TwoFactorEnrollment>('/auth/2fa/enroll/', payload)
    return data
  },

  confirmEnrollment: async (code: string): Promise<RecoveryCodesResponse> => {
    const { data } = await apiClient.post<RecoveryCodesResponse>('/auth/2fa/enroll/confirm/', { code })
    return data
  },

  /** The old batch stops working. */
  regenerateRecoveryCodes: async (code: string): Promise<RecoveryCodesResponse> => {
    const { data } = await apiClient.post<RecoveryCodesResponse>('/auth/2fa/recovery-codes/', { code })
    return data
  },

  disable: async (payload: DisablePayload): Promise<void> => {
    await apiClient.delete('/auth/2fa/', { data: payload })
  },
}
